import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Battery,
  Lightbulb,
  Zap,
  Activity,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Plus,
  Settings,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  RotateCcw,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  BarChart3,
  PieChart,
  Users,
  Server,
  Database,
  Cpu,
  HardDrive,
  Wifi,
  DollarSign,
  Calendar,
  Download,
  Upload,
  Search,
  Filter
} from 'lucide-react';
import AIControlSystem from '../components/AIControlSystem';
import { useScanLanguage } from '../contexts/LanguageContext';

// Energy Station Interfaces
interface EnergyStation {
  id: string;
  name: string;
  location: string;
  coordinates: { lat: number; lng: number };
  type: 'solar' | 'wind' | 'hydro' | 'nuclear' | 'thermal';
  capacity: number; // MW
  currentOutput: number; // MW
  efficiency: number; // %
  status: 'online' | 'offline' | 'maintenance' | 'error';
  lastMaintenance: string;
  nextMaintenance: string;
  totalGenerated: number; // MWh
  carbonOffset: number; // tons CO2
  operationalCost: number; // USD per MWh
  revenue: number; // USD
  connectedNodes: number;
  gridStability: number; // %
  createdAt: string;
}

interface EnergyMetrics {
  totalCapacity: number;
  totalOutput: number;
  averageEfficiency: number;
  totalStations: number;
  onlineStations: number;
  totalRevenue: number;
  carbonSaved: number;
  gridStability: number;
}

// BSS Interfaces
interface BSSNode {
  id: string;
  name: string;
  location: string;
  coordinates: { lat: number; lng: number };
  type: 'primary' | 'secondary' | 'backup';
  capacity: number; // kWh
  currentCharge: number; // kWh
  chargeLevel: number; // %
  status: 'charging' | 'discharging' | 'idle' | 'maintenance' | 'error';
  temperature: number; // Celsius
  voltage: number; // V
  current: number; // A
  cycleCount: number;
  health: number; // %
  lastService: string;
  nextService: string;
  connectedDevices: number;
  energyThroughput: number; // kWh
  efficiency: number; // %
  operationalHours: number;
  createdAt: string;
}

interface BSSMetrics {
  totalNodes: number;
  totalCapacity: number;
  totalCharge: number;
  averageHealth: number;
  activeNodes: number;
  totalThroughput: number;
  averageEfficiency: number;
  totalDevices: number;
}

const EnergyBSSPage: React.FC = () => {
  const { t } = useScanLanguage();
  const [activeTab, setActiveTab] = useState<'energy' | 'bss'>('energy');

  const statusLabel = (status: string) => {
    const key = `energy.status.${status}`;
    const label = t(key);
    return label === key ? status : label;
  };

  const typeLabel = (type: string) => {
    const key = `energy.type.${type}`;
    const label = t(key);
    return label === key ? type.toUpperCase() : label;
  };
  const [energyStations, setEnergyStations] = useState<EnergyStation[]>([]);
  const [bssNodes, setBssNodes] = useState<BSSNode[]>([]);
  const [energyMetrics, setEnergyMetrics] = useState<EnergyMetrics | null>(null);
  const [bssMetrics, setBssMetrics] = useState<BSSMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<EnergyStation | BSSNode | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAIControl, setShowAIControl] = useState(false);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // 30초마다 업데이트
    return () => clearInterval(interval);
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'energy') {
        await loadEnergyData();
      } else {
        await loadBSSData();
      }
      
      setLoading(false);
    } catch (error) {
      console.error('데이터 로딩 실패:', error);
      setLoading(false);
    }
  };

  const loadEnergyData = async () => {
    // Mock Energy Station Data
    const mockStations: EnergyStation[] = [
      {
        id: 'energy-1',
        name: 'Voltron Solar Farm Alpha',
        location: 'Jakarta Pusat, Jakarta, Indonesia',
        coordinates: { lat: -6.2088, lng: 106.8456 },
        type: 'solar',
        capacity: 500,
        currentOutput: 425,
        efficiency: 85,
        status: 'online',
        lastMaintenance: '2024-02-15T00:00:00Z',
        nextMaintenance: '2024-05-15T00:00:00Z',
        totalGenerated: 125000,
        carbonOffset: 62500,
        operationalCost: 45,
        revenue: 2500000,
        connectedNodes: 156,
        gridStability: 98.5,
        createdAt: '2023-06-01T00:00:00Z'
      },
      {
        id: 'energy-2',
        name: 'Wind Power Station Beta',
        location: 'Bali, Indonesia',
        coordinates: { lat: -8.3405, lng: 115.0920 },
        type: 'wind',
        capacity: 300,
        currentOutput: 280,
        efficiency: 93.3,
        status: 'online',
        lastMaintenance: '2024-01-20T00:00:00Z',
        nextMaintenance: '2024-04-20T00:00:00Z',
        totalGenerated: 89000,
        carbonOffset: 44500,
        operationalCost: 38,
        revenue: 1800000,
        connectedNodes: 98,
        gridStability: 96.2,
        createdAt: '2023-08-15T00:00:00Z'
      },
      {
        id: 'energy-3',
        name: 'Hydro Station Gamma',
        location: 'Yogyakarta, Central Java, Indonesia',
        coordinates: { lat: -7.7956, lng: 110.3695 },
        type: 'hydro',
        capacity: 750,
        currentOutput: 650,
        efficiency: 86.7,
        status: 'maintenance',
        lastMaintenance: '2024-03-01T00:00:00Z',
        nextMaintenance: '2024-06-01T00:00:00Z',
        totalGenerated: 180000,
        carbonOffset: 90000,
        operationalCost: 32,
        revenue: 3200000,
        connectedNodes: 234,
        gridStability: 99.1,
        createdAt: '2023-04-10T00:00:00Z'
      }
    ];

    const mockEnergyMetrics: EnergyMetrics = {
      totalCapacity: mockStations.reduce((sum, station) => sum + station.capacity, 0),
      totalOutput: mockStations.reduce((sum, station) => sum + station.currentOutput, 0),
      averageEfficiency: mockStations.reduce((sum, station) => sum + station.efficiency, 0) / mockStations.length,
      totalStations: mockStations.length,
      onlineStations: mockStations.filter(s => s.status === 'online').length,
      totalRevenue: mockStations.reduce((sum, station) => sum + station.revenue, 0),
      carbonSaved: mockStations.reduce((sum, station) => sum + station.carbonOffset, 0),
      gridStability: mockStations.reduce((sum, station) => sum + station.gridStability, 0) / mockStations.length
    };

    setEnergyStations(mockStations);
    setEnergyMetrics(mockEnergyMetrics);
  };

  const loadBSSData = async () => {
    // Mock BSS Node Data
    const mockNodes: BSSNode[] = [
      {
        id: 'bss-1',
        name: 'BSS Hub Central',
        location: 'Jakarta Pusat, Jakarta, Indonesia',
        coordinates: { lat: -6.2088, lng: 106.8456 },
        type: 'primary',
        capacity: 1000,
        currentCharge: 850,
        chargeLevel: 85,
        status: 'charging',
        temperature: 25,
        voltage: 400,
        current: 125,
        cycleCount: 1250,
        health: 95,
        lastService: '2024-02-10T00:00:00Z',
        nextService: '2024-05-10T00:00:00Z',
        connectedDevices: 45,
        energyThroughput: 25000,
        efficiency: 92,
        operationalHours: 8760,
        createdAt: '2023-05-15T00:00:00Z'
      },
      {
        id: 'bss-2',
        name: 'BSS Station North',
        location: 'Bandung, West Java, Indonesia',
        coordinates: { lat: -6.9175, lng: 107.6191 },
        type: 'secondary',
        capacity: 500,
        currentCharge: 320,
        chargeLevel: 64,
        status: 'discharging',
        temperature: 28,
        voltage: 380,
        current: 85,
        cycleCount: 890,
        health: 88,
        lastService: '2024-01-25T00:00:00Z',
        nextService: '2024-04-25T00:00:00Z',
        connectedDevices: 28,
        energyThroughput: 15000,
        efficiency: 89,
        operationalHours: 7200,
        createdAt: '2023-07-20T00:00:00Z'
      },
      {
        id: 'bss-3',
        name: 'BSS Backup South',
        location: 'Surabaya, East Java, Indonesia',
        coordinates: { lat: -7.2575, lng: 112.7521 },
        type: 'backup',
        capacity: 750,
        currentCharge: 600,
        chargeLevel: 80,
        status: 'idle',
        temperature: 22,
        voltage: 410,
        current: 0,
        cycleCount: 456,
        health: 98,
        lastService: '2024-03-05T00:00:00Z',
        nextService: '2024-06-05T00:00:00Z',
        connectedDevices: 12,
        energyThroughput: 8500,
        efficiency: 94,
        operationalHours: 4380,
        createdAt: '2023-09-10T00:00:00Z'
      },
      {
        id: 'bss-4',
        name: 'BSS Station Medan',
        location: 'Medan, North Sumatra, Indonesia',
        coordinates: { lat: 3.5952, lng: 98.6722 },
        type: 'primary',
        capacity: 1200,
        currentCharge: 980,
        chargeLevel: 82,
        status: 'charging',
        temperature: 26,
        voltage: 405,
        current: 135,
        cycleCount: 1100,
        health: 93,
        lastService: '2024-02-20T00:00:00Z',
        nextService: '2024-05-20T00:00:00Z',
        connectedDevices: 52,
        energyThroughput: 28000,
        efficiency: 91,
        operationalHours: 8200,
        createdAt: '2023-06-10T00:00:00Z'
      },
      {
        id: 'bss-5',
        name: 'BSS Hub Makassar',
        location: 'Makassar, South Sulawesi, Indonesia',
        coordinates: { lat: -5.1477, lng: 119.4327 },
        type: 'secondary',
        capacity: 800,
        currentCharge: 520,
        chargeLevel: 65,
        status: 'discharging',
        temperature: 30,
        voltage: 375,
        current: 95,
        cycleCount: 720,
        health: 87,
        lastService: '2024-01-15T00:00:00Z',
        nextService: '2024-04-15T00:00:00Z',
        connectedDevices: 35,
        energyThroughput: 18000,
        efficiency: 88,
        operationalHours: 6500,
        createdAt: '2023-08-25T00:00:00Z'
      },
      {
        id: 'bss-6',
        name: 'BSS Node Denpasar',
        location: 'Denpasar, Bali, Indonesia',
        coordinates: { lat: -8.6705, lng: 115.2126 },
        type: 'backup',
        capacity: 600,
        currentCharge: 480,
        chargeLevel: 80,
        status: 'idle',
        temperature: 24,
        voltage: 395,
        current: 0,
        cycleCount: 380,
        health: 96,
        lastService: '2024-03-10T00:00:00Z',
        nextService: '2024-06-10T00:00:00Z',
        connectedDevices: 18,
        energyThroughput: 12000,
        efficiency: 93,
        operationalHours: 3800,
        createdAt: '2023-10-05T00:00:00Z'
      }
    ];

    const mockBSSMetrics: BSSMetrics = {
      totalNodes: mockNodes.length,
      totalCapacity: mockNodes.reduce((sum, node) => sum + node.capacity, 0),
      totalCharge: mockNodes.reduce((sum, node) => sum + node.currentCharge, 0),
      averageHealth: mockNodes.reduce((sum, node) => sum + node.health, 0) / mockNodes.length,
      activeNodes: mockNodes.filter(n => n.status !== 'maintenance' && n.status !== 'error').length,
      totalThroughput: mockNodes.reduce((sum, node) => sum + node.energyThroughput, 0),
      averageEfficiency: mockNodes.reduce((sum, node) => sum + node.efficiency, 0) / mockNodes.length,
      totalDevices: mockNodes.reduce((sum, node) => sum + node.connectedDevices, 0)
    };

    setBssNodes(mockNodes);
    setBssMetrics(mockBSSMetrics);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
      case 'charging':
      case 'discharging':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'offline':
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'idle':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
      case 'charging':
      case 'discharging':
        return <CheckCircle className="w-4 h-4" />;
      case 'offline':
      case 'error':
        return <XCircle className="w-4 h-4" />;
      case 'maintenance':
        return <Settings className="w-4 h-4" />;
      case 'idle':
        return <Clock className="w-4 h-4" />;
      default:
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'solar': return 'bg-yellow-100 text-yellow-800';
      case 'wind': return 'bg-blue-100 text-blue-800';
      case 'hydro': return 'bg-cyan-100 text-cyan-800';
      case 'nuclear': return 'bg-purple-100 text-purple-800';
      case 'thermal': return 'bg-red-100 text-red-800';
      case 'primary': return 'bg-green-100 text-green-800';
      case 'secondary': return 'bg-blue-100 text-blue-800';
      case 'backup': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  const currentData = activeTab === 'energy' ? energyStations : bssNodes;
  const currentMetrics = activeTab === 'energy' ? energyMetrics : bssMetrics;

  const filteredData = activeTab === 'energy' 
    ? energyStations.filter((item: EnergyStation) => {
        const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             item.location.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
        const matchesType = typeFilter === 'all' || item.type === typeFilter;
        return matchesSearch && matchesStatus && matchesType;
      })
    : bssNodes.filter((item: BSSNode) => {
        const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             item.location.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
        const matchesType = typeFilter === 'all' || item.type === typeFilter;
        return matchesSearch && matchesStatus && matchesType;
      });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('energy.title')}</h1>
            <p className="text-gray-600 mt-1">{t('energy.subtitle')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{activeTab === 'energy' ? t('energy.addStation') : t('energy.addBss')}</span>
            </button>
            <button
              onClick={loadData}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>{t('energy.refresh')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'energy', name: t('energy.tabEnergy'), icon: Battery, color: 'text-yellow-600' },
              { id: 'bss', name: t('energy.tabBss'), icon: Lightbulb, color: 'text-blue-600' }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? `border-orange-500 ${tab.color}`
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                    {currentData.length}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* 통계 카드 */}
          {currentMetrics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              {activeTab === 'energy' ? (
                <>
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-yellow-100">{t('energy.metricTotalCapacity')}</p>
                        <p className="text-2xl font-bold">{formatNumber((energyMetrics as EnergyMetrics).totalCapacity)} MW</p>
                      </div>
                      <Battery className="w-8 h-8 text-yellow-200" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-green-400 to-green-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100">{t('energy.metricCurrentOutput')}</p>
                        <p className="text-2xl font-bold">{formatNumber((energyMetrics as EnergyMetrics).totalOutput)} MW</p>
                      </div>
                      <Zap className="w-8 h-8 text-green-200" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-blue-400 to-blue-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100">{t('energy.metricAvgEfficiency')}</p>
                        <p className="text-2xl font-bold">{(energyMetrics as EnergyMetrics).averageEfficiency.toFixed(1)}%</p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-blue-200" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-400 to-purple-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100">{t('energy.metricTotalRevenue')}</p>
                        <p className="text-2xl font-bold">${formatNumber((energyMetrics as EnergyMetrics).totalRevenue / 1000000)}M</p>
                      </div>
                      <DollarSign className="w-8 h-8 text-purple-200" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-gradient-to-r from-blue-400 to-blue-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100">{t('energy.metricTotalCapacity')}</p>
                        <p className="text-2xl font-bold">{formatNumber((bssMetrics as BSSMetrics).totalCapacity)} kWh</p>
                      </div>
                      <Database className="w-8 h-8 text-blue-200" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-green-400 to-green-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100">{t('energy.metricCurrentCharge')}</p>
                        <p className="text-2xl font-bold">{formatNumber((bssMetrics as BSSMetrics).totalCharge)} kWh</p>
                      </div>
                      <Battery className="w-8 h-8 text-green-200" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-orange-400 to-orange-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100">{t('energy.metricAvgHealth')}</p>
                        <p className="text-2xl font-bold">{(bssMetrics as BSSMetrics).averageHealth.toFixed(1)}%</p>
                      </div>
                      <Activity className="w-8 h-8 text-orange-200" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-400 to-purple-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100">{t('energy.metricConnectedDevices')}</p>
                        <p className="text-2xl font-bold">{formatNumber((bssMetrics as BSSMetrics).totalDevices)}</p>
                      </div>
                      <Users className="w-8 h-8 text-purple-200" />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* 필터 */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={t('energy.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">{t('energy.filterAllStatus')}</option>
                {activeTab === 'energy' ? (
                  <>
                    <option value="online">{t('energy.status.online')}</option>
                    <option value="offline">{t('energy.status.offline')}</option>
                    <option value="maintenance">{t('energy.status.maintenance')}</option>
                    <option value="error">{t('energy.status.error')}</option>
                  </>
                ) : (
                  <>
                    <option value="charging">{t('energy.status.charging')}</option>
                    <option value="discharging">{t('energy.status.discharging')}</option>
                    <option value="idle">{t('energy.status.idle')}</option>
                    <option value="maintenance">{t('energy.status.maintenance')}</option>
                    <option value="error">{t('energy.status.error')}</option>
                  </>
                )}
              </select>
              
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">{t('energy.filterAllTypes')}</option>
                {activeTab === 'energy' ? (
                  <>
                    <option value="solar">{t('energy.type.solar')}</option>
                    <option value="wind">{t('energy.type.wind')}</option>
                    <option value="hydro">{t('energy.type.hydro')}</option>
                    <option value="nuclear">{t('energy.type.nuclear')}</option>
                    <option value="thermal">{t('energy.type.thermal')}</option>
                  </>
                ) : (
                  <>
                    <option value="primary">{t('energy.type.primary')}</option>
                    <option value="secondary">{t('energy.type.secondary')}</option>
                    <option value="backup">{t('energy.type.backup')}</option>
                  </>
                )}
              </select>
              
              <div className="text-sm text-gray-600 flex items-center">
                {t('energy.itemsShown', { n: filteredData.length })}
              </div>
            </div>
          </div>

          {/* 데이터 테이블 */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {activeTab === 'energy' ? (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colStation')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colType')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colStatus')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colCapacity')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colEfficiency')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colRevenue')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colActions')}</th>
                    </>
                  ) : (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colNode')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colType')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colStatus')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colCharge')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colEfficiency')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colDevices')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('energy.colActions')}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((item: any) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    {activeTab === 'energy' ? (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                              <Battery className="w-4 h-4 text-yellow-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{item.name}</p>
                              <p className="text-sm text-gray-500">{item.location}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${getTypeColor(item.type)}`}>
                            {typeLabel(item.type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(item.status)}`}>
                            {getStatusIcon(item.status)}
                            <span>{statusLabel(item.status)}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <p className="font-medium text-gray-900">{(item as EnergyStation).currentOutput} / {(item as EnergyStation).capacity} MW</p>
                            <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${((item as EnergyStation).currentOutput / (item as EnergyStation).capacity) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-gray-900">{(item as EnergyStation).efficiency}%</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-green-600">${formatNumber((item as EnergyStation).revenue / 1000)}K</span>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Lightbulb className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{item.name}</p>
                              <p className="text-sm text-gray-500">{item.location}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${getTypeColor(item.type)}`}>
                            {typeLabel(item.type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(item.status)}`}>
                            {getStatusIcon(item.status)}
                            <span>{statusLabel(item.status)}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <p className="font-medium text-gray-900">{(item as BSSNode).currentCharge} / {(item as BSSNode).capacity} kWh</p>
                            <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${(item as BSSNode).chargeLevel}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-gray-900">{(item as BSSNode).health}%</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-blue-600">{(item as BSSNode).connectedDevices}</span>
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => {
                            setSelectedItem(item);
                            setShowAIControl(true);
                          }}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg"
                          title={t('energy.action.aiControl')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-lg"
                          title={t('energy.action.edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-100 rounded-lg"
                          title={t('energy.action.settings')}
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg"
                          title={t('energy.action.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* AI 관제 시스템 모달 */}
      {showAIControl && selectedItem && (
        <AIControlSystem
          stationId={selectedItem.id}
          stationName={selectedItem.name}
          stationType={activeTab === 'energy' ? 'energy' : 'bss'}
          onClose={() => {
            setShowAIControl(false);
            setSelectedItem(null);
          }}
        />
      )}

      {/* 에너지 스테이션/BSS 노드 추가 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {activeTab === 'energy' ? t('energy.modal.addStation') : t('energy.modal.addBss')}
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('energy.modal.name')}</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder={activeTab === 'energy' ? t('energy.modal.placeholderNameStation') : t('energy.modal.placeholderNameBss')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('energy.modal.location')}</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder={t('energy.modal.placeholderLocation')}
                />
              </div>
              
              {activeTab === 'energy' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('energy.modal.type')}</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                      <option value="solar">SOLAR</option>
                      <option value="wind">WIND</option>
                      <option value="hydro">HYDRO</option>
                      <option value="nuclear">NUCLEAR</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('energy.modal.capacityMw')}</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder={t('energy.modal.placeholderCapacity')}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('energy.modal.type')}</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                      <option value="primary">{t('energy.type.primary')}</option>
                      <option value="secondary">{t('energy.type.secondary')}</option>
                      <option value="backup">{t('energy.type.backup')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('energy.modal.capacityKwh')}</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder={t('energy.modal.placeholderCapacity')}
                    />
                  </div>
                </>
              )}
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('energy.modal.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  {t('energy.modal.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default EnergyBSSPage;